import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import "./About.css";
import profileFallback from "./profile.jpg";
import { applyGlitchEffect } from "./glitchText";

/**
 * Tries multiple image paths (in order) and hides if none load.
 * IMPORTANT: These paths are assumed to be in /public (or /public/images).
 */
function SmartImage({ candidates = [], alt = "", className = "" }) {
  const [idx, setIdx] = useState(0);
  const [hidden, setHidden] = useState(false);

  const src = useMemo(() => {
    if (!candidates?.length) return null;
    const p = candidates[idx];
    if (!p) return null;

    // If it already looks absolute or starts with /, respect it.
    if (p.startsWith("/") || p.startsWith("http")) return p;

    return `${process.env.PUBLIC_URL}/${p}`;
  }, [candidates, idx]);

  if (!src || hidden) return null;

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (idx < candidates.length - 1) setIdx((v) => v + 1);
        else setHidden(true);
      }}
      draggable={false}
    />
  );
}

/** NEW: Skill timeline map (Y=years, X=skills) — per active category */
function SkillTimelineMap({ active, skillData, selected, onSelect }) {
  const yearsMax = 8;
  const ticks = [0, 2, 4, 6, 8];

  const list = skillData[active] || [];

  // Bubble position
  const yToPx = (years, height) => {
    const y = Math.max(0, Math.min(yearsMax, Number(years) || 0));
    // top padding 16, bottom padding 24
    const topPad = 16;
    const bottomPad = 28;
    const usable = Math.max(1, height - topPad - bottomPad);
    // 0 years at bottom, max at top
    return topPad + (1 - y / yearsMax) * usable;
  };

  return (
    <div className={`skillMap skillMap--${active.toLowerCase()}`}>
      <div className="skillMapHeader">
        <div>
          <div className="skillMapTitle">Skill Timeline</div>
          <div className="skillMapSub">Y-axis: years • X-axis: skills • click a bubble for details</div>
        </div>

        <div className="skillMapLegend">
          <span className={`legendDot legendDot--${active.toLowerCase()}`} />
          <span className="legendText">{active}</span>
        </div>
      </div>

      <div className="skillMapViewport">
        {/* Y axis */}
        <div className="skillMapYAxis">
          <div className="skillMapYAxisLabel">Years</div>
          {ticks
            .slice()
            .reverse()
            .map((t) => (
              <div key={t} className="skillMapTick">
                <span className="skillMapTickLabel">{t}</span>
              </div>
            ))}
        </div>

        {/* Scrollable plot */}
        <div className="skillMapPlotOuter">
          <div className="skillMapPlot" style={{ "--cols": list.length }}>
            {/* Grid lines */}
            <div className="skillMapGrid">
              {ticks
                .slice()
                .reverse()
                .map((t) => (
                  <div key={t} className="skillMapGridLine">
                    <span className="skillMapGridLineLabel">{t}</span>
                  </div>
                ))}
            </div>

            {/* Bubbles */}
            <div className="skillMapCols">
              {list.map((s) => (
                <div key={s.name} className="skillCol">
                  <SkillBubble
                    active={active}
                    skill={s}
                    selected={selected?.name === s.name}
                    onSelect={onSelect}
                    yToPx={yToPx}
                  />
                  <div className="skillColLabel" title={s.name}>
                    {s.short || s.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Details panel */}
      <div className="skillDetails">
        {selected ? (
          <>
            <div className="skillDetailsTop">
              <div className="skillDetailsName">{selected.name}</div>
              <div className="skillDetailsYears">{selected.years} yrs</div>
            </div>
            <div className="skillDetailsDesc">{selected.desc}</div>

            {selected.tags?.length ? (
              <div className="skillDetailsTags">
                {selected.tags.map((t) => (
                  <span key={t} className="chip chip--mini">
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="skillDetailsEmpty">Click a bubble to see what it represents.</div>
        )}
      </div>
    </div>
  );
}

function SkillBubble({ active, skill, selected, onSelect, yToPx }) {
  const [hover, setHover] = useState(false);
  const years = Number(skill.years) || 0;

  return (
    <div className="skillBubbleWrap">
      <div
        className={`skillBubble skillBubble--${active.toLowerCase()} ${selected ? "isSelected" : ""}`}
        style={{ top: `calc(${yToPx(years, 260)}px)` }}
        role="button"
        tabIndex={0}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => onSelect(skill)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelect(skill);
        }}
        title={`${skill.name} • ${years} yrs`}
        aria-label={`${skill.name}, ${years} years`}
      >
        <span className="skillBubbleText">{skill.icon || skill.short || "•"}</span>
      </div>

      {hover ? (
        <div className="skillTooltip">
          <div className="skillTooltipName">{skill.name}</div>
          <div className="skillTooltipMeta">{years} yrs</div>
        </div>
      ) : null}
    </div>
  );
}

/** ✅ YOUR SkillTree upgraded (tree click switches categories + new timeline map below) */
function SkillTree({ active = "DATA", onChange = () => {} }) {
  // Skill data: years + short label + descriptions
  const skillData = useMemo(
    () => ({
      DATA: [
        { name: "Python", short: "PY", icon: "PY", years: 6.5, desc: "Pipelines, automation, validation tooling, and analytics enablement.", tags: ["ETL", "Automation", "Quality"] },
        { name: "SQL", short: "SQL", icon: "SQL", years: 6.5, desc: "Complex querying, performance tuning, and modeling for BI/analytics.", tags: ["Tuning", "Modeling"] },
        { name: "Apache Airflow", short: "AF", icon: "AF", years: 4.0, desc: "Orchestration, DAG design, retries/SLAs, backfills, and operational ownership.", tags: ["Orchestration", "SLAs"] },
        { name: "MWAA", short: "MWAA", icon: "MW", years: 3.0, desc: "Managed Airflow on AWS: env lifecycle, plugins, monitoring, and cost/scale hygiene.", tags: ["AWS", "Managed"] },
        { name: "ETL/ELT", short: "ETL", icon: "ETL", years: 6.0, desc: "Ingestion → transform → curated layers, with runbooks and production monitoring.", tags: ["Pipelines", "Ops"] },
        { name: "Data Modeling", short: "DM", icon: "DM", years: 5.0, desc: "Dimensional & domain modeling to create stable analytics datasets.", tags: ["Dimensional", "Semantic"] },
        { name: "Hadoop/HDFS", short: "HDFS", icon: "HD", years: 4.5, desc: "Hybrid pipelines, batch processing, and ecosystem operations.", tags: ["Hybrid", "Batch"] },
        { name: "Impala", short: "IM", icon: "IM", years: 4.0, desc: "Governed SQL access layers, external tables, and query performance.", tags: ["SQL", "Governance"] },
        { name: "Cloudera", short: "CDP", icon: "CD", years: 4.0, desc: "Platform workflows, environments, and production support at scale.", tags: ["Platform", "Ops"] },
        { name: "Data Quality", short: "DQ", icon: "DQ", years: 5.0, desc: "Checks, standards, and trusted datasets (contracts, validation, monitoring).", tags: ["Checks", "Trust"] },
      ],
      CLOUD: [
        { name: "AWS", short: "AWS", icon: "AWS", years: 6.0, desc: "Cloud delivery across data platforms, governance, and operations.", tags: ["Cloud", "Delivery"] },
        { name: "S3", short: "S3", icon: "S3", years: 6.0, desc: "Data lakes, lifecycle policies, access patterns, and cost controls.", tags: ["Lake", "Cost"] },
        { name: "RDS", short: "RDS", icon: "RDS", years: 5.0, desc: "Relational persistence, tuning, backups, and reliability practices.", tags: ["Relational", "Reliability"] },
        { name: "DynamoDB", short: "DDB", icon: "DDB", years: 3.0, desc: "Key-value workloads, access patterns, and scaling/monitoring.", tags: ["NoSQL", "Scale"] },
        { name: "Redshift", short: "RS", icon: "RS", years: 4.5, desc: "Warehouse patterns, performance, and analytics enablement.", tags: ["Warehouse", "BI"] },
        { name: "Terraform", short: "TF", icon: "TF", years: 4.0, desc: "Infrastructure as code: repeatable environments + guardrails.", tags: ["IaC", "Guardrails"] },
        { name: "IAM", short: "IAM", icon: "IAM", years: 5.0, desc: "Least privilege, roles/policies, and secure access controls.", tags: ["Security", "Access"] },
        { name: "Monitoring/Alerts", short: "MON", icon: "MN", years: 5.0, desc: "Operational ownership: dashboards, alerts, runbooks, and SLAs.", tags: ["Ops", "SLA"] },
      ],
      SOFTWARE: [
        { name: "C#", short: "C#", icon: "C#", years: 5.0, desc: "Backend services, tooling, and enterprise delivery.", tags: ["Backend", "Enterprise"] },
        { name: ".NET", short: ".NET", icon: ".N", years: 5.0, desc: "Service patterns, integrations, and maintainable architectures.", tags: ["Services", "Patterns"] },
        { name: "APIs", short: "API", icon: "API", years: 6.0, desc: "RESTful integration, contracts, and reliability practices.", tags: ["REST", "Contracts"] },
        { name: "React", short: "RE", icon: "RE", years: 3.5, desc: "UI components, portfolio apps, and interactive experiences.", tags: ["UI", "Components"] },
        { name: "Angular", short: "NG", icon: "NG", years: 3.0, desc: "Enterprise UI delivery, modular features, and maintainability.", tags: ["UI", "Enterprise"] },
        { name: "CI/CD", short: "CI", icon: "CI", years: 4.0, desc: "Build/release pipelines, quality gates, and automation.", tags: ["Pipelines", "Automation"] },
        { name: "Debugging", short: "DBG", icon: "DB", years: 6.0, desc: "Production support, root cause analysis, and performance tuning.", tags: ["RCA", "Perf"] },
        { name: "Agile Delivery", short: "AG", icon: "AG", years: 7.0, desc: "Iterative delivery, stakeholder alignment, and execution rhythm.", tags: ["Agile", "Execution"] },
      ],
    }),
    []
  );

  const [selected, setSelected] = useState(null);

  // Keep selected skill consistent per category
  const ensureSelected = (cat) => {
    const list = skillData[cat] || [];
    if (!list.length) return setSelected(null);
    setSelected((prev) => {
      if (prev && list.some((x) => x.name === prev.name)) return prev;
      return list[0];
    });
  };

  const setActive = (cat) => {
    onChange(cat);
    ensureSelected(cat);
  };

  // Initialize selection once
  React.useEffect(() => {
    if (!selected) ensureSelected(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When active changes (via pills or tree click)
  React.useEffect(() => {
    ensureSelected(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <div className="aboutSectionCard">
      <div className="aboutSectionHeader">
        <div>
          <div className="aboutSectionTitle">Skills</div>
          <div className="aboutSectionSub">Click the tree or the pills — both switch the same view.</div>
        </div>

        <div className="aboutPills" role="tablist" aria-label="Skill categories">
          {["DATA", "CLOUD", "SOFTWARE"].map((k) => (
            <button
              key={k}
              type="button"
              className={`aboutPill ${active === k ? "active" : ""}`}
              onClick={() => setActive(k)}
              role="tab"
              aria-selected={active === k}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className={`skillTree skillTree--${active.toLowerCase()}`}>
        <div className="skillTree__canvas">
          <svg className="skillTree__svg" viewBox="0 0 1200 520" preserveAspectRatio="xMidYMid meet">
            <defs>
              <filter id="glowCyan">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="glowGold">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="glowRed">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Branches (clickable) */}
            <path
              className="branch branch--data treeHot"
              d="M 320 420 C 260 330, 220 290, 160 250 C 120 220, 100 180, 120 140"
              onClick={() => setActive("DATA")}
            />
            <path
              className="branch branch--data treeHot"
              d="M 320 420 C 290 360, 300 310, 360 270 C 420 230, 460 190, 500 140"
              onClick={() => setActive("DATA")}
            />
            <path
              className="branch branch--cloud treeHot"
              d="M 600 450 C 600 360, 600 300, 600 240 C 600 190, 640 160, 700 150"
              onClick={() => setActive("CLOUD")}
            />
            <path
              className="branch branch--software treeHot"
              d="M 880 420 C 940 330, 980 290, 1040 250 C 1080 220, 1100 180, 1080 140"
              onClick={() => setActive("SOFTWARE")}
            />
            <path
              className="branch branch--software treeHot"
              d="M 880 420 C 910 360, 900 310, 840 270 C 780 230, 740 190, 700 150"
              onClick={() => setActive("SOFTWARE")}
            />

            {/* Animated “flow” overlays */}
            <path className="flow flow--data" d="M 320 420 C 260 330, 220 290, 160 250 C 120 220, 100 180, 120 140" />
            <path className="flow flow--data" d="M 320 420 C 290 360, 300 310, 360 270 C 420 230, 460 190, 500 140" />
            <path className="flow flow--cloud" d="M 600 450 C 600 360, 600 300, 600 240 C 600 190, 640 160, 700 150" />
            <path className="flow flow--software" d="M 880 420 C 940 330, 980 290, 1040 250 C 1080 220, 1100 180, 1080 140" />
            <path className="flow flow--software" d="M 880 420 C 910 360, 900 310, 840 270 C 780 230, 740 190, 700 150" />

            {/* Nodes (clickable by category) */}
            <g className="node node--data treeHot" transform="translate(320 420)" onClick={() => setActive("DATA")}>
              <circle className="node__ring" r="26" />
              <circle className="node__core" r="14" />
              <text className="node__txt" y="5">PY</text>
            </g>
            <g className="node node--data node--small treeHot" transform="translate(240 350)" onClick={() => setActive("DATA")}>
              <circle className="node__ring" r="20" />
              <circle className="node__core" r="11" />
              <text className="node__txt" y="4">SQL</text>
            </g>
            <g className="node node--data node--small treeHot" transform="translate(170 255)" onClick={() => setActive("DATA")}>
              <circle className="node__ring" r="20" />
              <circle className="node__core" r="11" />
              <text className="node__txt" y="4">DQ</text>
            </g>
            <g className="node node--data node--locked treeHot" transform="translate(120 140)" onClick={() => setActive("DATA")}>
              <circle className="node__ring" r="22" />
              <circle className="node__core" r="12" />
              <text className="node__txt" y="4">ETL</text>
            </g>

            <g className="node node--cloud treeHot" transform="translate(600 450)" onClick={() => setActive("CLOUD")}>
              <circle className="node__ring" r="26" />
              <circle className="node__core" r="14" />
              <text className="node__txt" y="5">AWS</text>
            </g>
            <g className="node node--cloud node--small treeHot" transform="translate(600 330)" onClick={() => setActive("CLOUD")}>
              <circle className="node__ring" r="20" />
              <circle className="node__core" r="11" />
              <text className="node__txt" y="4">IaC</text>
            </g>
            <g className="node node--cloud node--small treeHot" transform="translate(600 240)" onClick={() => setActive("CLOUD")}>
              <circle className="node__ring" r="20" />
              <circle className="node__core" r="11" />
              <text className="node__txt" y="4">OPS</text>
            </g>
            <g className="node node--cloud node--locked treeHot" transform="translate(700 150)" onClick={() => setActive("CLOUD")}>
              <circle className="node__ring" r="22" />
              <circle className="node__core" r="12" />
              <text className="node__txt" y="4">SEC</text>
            </g>

            <g className="node node--software treeHot" transform="translate(880 420)" onClick={() => setActive("SOFTWARE")}>
              <circle className="node__ring" r="26" />
              <circle className="node__core" r="14" />
              <text className="node__txt" y="5">C#</text>
            </g>
            <g className="node node--software node--small treeHot" transform="translate(960 340)" onClick={() => setActive("SOFTWARE")}>
              <circle className="node__ring" r="20" />
              <circle className="node__core" r="11" />
              <text className="node__txt" y="4">API</text>
            </g>
            <g className="node node--software node--small treeHot" transform="translate(1040 250)" onClick={() => setActive("SOFTWARE")}>
              <circle className="node__ring" r="20" />
              <circle className="node__core" r="11" />
              <text className="node__txt" y="4">UI</text>
            </g>
            <g className="node node--software node--locked treeHot" transform="translate(1080 140)" onClick={() => setActive("SOFTWARE")}>
              <circle className="node__ring" r="22" />
              <circle className="node__core" r="12" />
              <text className="node__txt" y="4">MVC</text>
            </g>

            {/* Labels (clickable) */}
            <text className="treeLabel treeLabel--data treeHot" x="120" y="500" onClick={() => setActive("DATA")}>
              DATA
            </text>
            <text className="treeLabel treeLabel--cloud treeHot" x="565" y="500" onClick={() => setActive("CLOUD")}>
              CLOUD
            </text>
            <text className="treeLabel treeLabel--software treeHot" x="1010" y="500" onClick={() => setActive("SOFTWARE")}>
              SOFTWARE
            </text>
          </svg>
        </div>

        {/* NEW: Timeline map (Y=years, X=skills) */}
        <SkillTimelineMap
          active={active}
          skillData={skillData}
          selected={selected}
          onSelect={(skill) => setSelected(skill)}
        />
      </div>
    </div>
  );
}

/**
 * Section (no right-side images; fixes giant duplicates)
 */
function Section({ title, subtitle, children, open, onToggle }) {
  return (
    <div className={`aboutAccordion ${open ? "open" : ""}`}>
      <button type="button" className="aboutAccordionHeader" onClick={onToggle}>
        <div className="aboutAccordionHeader__text">
          <div className="aboutAccordionTitle">{title}</div>
          {subtitle ? <div className="aboutAccordionSub">{subtitle}</div> : null}
        </div>
        <div className="aboutAccordionHeader__right">
          <span className="aboutChevron">{open ? "−" : "+"}</span>
        </div>
      </button>

      <div className="aboutAccordionBody">{children}</div>
    </div>
  );
}

export default function About() {
  const [activeSkill, setActiveSkill] = useState("DATA");
  const [openKey, setOpenKey] = useState("bio");

  const heroCandidates = useMemo(
    () => ["images/linkedin.jpg", "images/profile.jpg", "linkedin.jpg", "profile.jpg"],
    []
  );

  // Original content (with images)
  const education = useMemo(
    () => [
      {
        degree: "Master of Science in Information Systems (Big Data & Analytics)",
        school: "Georgia State University",
        year: "2021",
        details:
          "Specialized in Big Data and Analytics, focusing on large-scale data infrastructure, cloud computing, and advanced data programming.",
        image: "images/georgiastate.png",
      },
      {
        degree: "Bachelor of Science in Software Development, Cum Laude",
        school: "Georgia Gwinnett College",
        year: "2017",
        details:
          "Graduated with honors, gaining a strong foundation in software engineering, system architecture, and full-stack development.",
        image: "images/georgiagwinnett.png",
      },
    ],
    []
  );

  const certifications = useMemo(
    () => [
      {
        name: "AWS Certified Solutions Architect – Associate",
        badge_url: "images/awssa.png",
        credly_url: "https://www.credly.com/badges/975862c4-d1e8-4b42-8947-b3e7c1053162",
      },
      {
        name: "AWS Certified Database – Specialty",
        badge_url: "images/awsd.png",
        credly_url: "https://www.credly.com/badges/cca37f0a-4798-427b-a5c7-4de8f9d462e7",
      },
      {
        name: "AWS Certified Data Engineer - Associate",
        badge_url: "images/awsde.png",
        credly_url: "https://www.credly.com/badges/01c5bad0-0f17-4611-8908-235b1b863e65",
      },
    ],
    []
  );

  const languages = useMemo(
    () => [
      {
        name: "Hrvatski",
        flag_image:
          "https://gallery.yopriceville.com/var/resizes/National-Flags/Croatia_Large_Flag.png?m=1441188629",
      },
      { name: "Српски", flag_image: "https://cdn.britannica.com/39/94839-004-EEB20F36/Flag-Serbia.jpg" },
      { name: "عربي", flag_image: "https://qph.cf2.quoracdn.net/main-qimg-c628f557e989248eca1467b5ee0fd070" },
      {
        name: "English",
        flag_image: "https://cdn.britannica.com/33/4833-004-828A9A84/Flag-United-States-of-America.jpg",
      },
    ],
    []
  );

  const music = useMemo(
    () => [
      { name: "KÖFN", album_image: "images/KÖFN.jpg" },
      { name: "Senidah", album_image: "images/Senidah.jpg" },
      { name: "GIMS", album_image: "images/GIMS.jpg" },
      { name: "Roosevelt", album_image: "images/Roosevelt.png" },
      { name: "Lomepal", album_image: "images/Lomepal.jpg" },
      { name: "Eagle Eyed Tiger", album_image: "images/Eagle Eyed Tiger.jpg"},
      { name: "Nek", album_image: "images/Nek.jpg" },
      { name: "Tame Impala", album_image: "images/Tame Impala.jpg" },
      { name: "ODESZA", album_image: "images/ODESZA.gif"},
      { name: "TR/ST", album_image: "images/TRST.jpg"},
      { name: "Grše", album_image: "images/Grše.jpg" },
      { name: "Simge", album_image: "images/Simge.jpg" },
    ],
    []
  );

  const citizenships = useMemo(
    () => [
      { name: "Croatian", flag_image: "images/croatia.png" },
      { name: "Serbian", flag_image: "images/Serbian.webp" },
      { name: "USA", flag_image: "images/USA.webp" },
    ],
    []
  );

  const games = useMemo(
    () => [
      { name: "Return to Castle Wolfenstein", game_image: "images/Wolfenstein.gif" },
      { name: "MGS5", game_image: "images/MGS5.png" },
      { name: "Frostpunk", game_image: "images/Frostpunk.gif" },
      { name: "Returnal", game_image: "images/Returnal.gif"},
      { name: "Skyrim", game_image: "images/Skyrim.jpg" },
      { name: "Civilization V", game_image: "images/Civilization V.jpg" },
      { name: "Total War Series", game_image: "images/Total War Series.jpg"},
      { name: "Tom Clancy's Splinter Cell", game_image: "images/Tom Clancy's Splinter Cell.jpg" },
      { name: "Call of Duty: Warzone", game_image: "images/Warzone.jpg"},
      { name: "Blasphemous 2", game_image: "images/Blasphemous.webp" },
      { name: "ARC Raiders", game_image: "images/arc.gif" },
    ],
    []
  );

  return (
    <div className="aboutPage">
      <Helmet>
        <title>About</title>
      </Helmet>

      <div className="aboutWrap">
        <div className="aboutCard">
          <div className="aboutHero">
            <div className="aboutHero__img">
              <SmartImage candidates={heroCandidates} alt="Khaled Asad" className="aboutHeroImg" />
              <img className="aboutHeroImg aboutHeroFallback" src={profileFallback} alt="Khaled Asad" />
            </div>

            <div className="aboutHero__text">
              <div className="aboutHero__name">Khaled Asad</div>
              <div className="aboutHero__bio">
                I am a Senior Data Engineer with 8 plus years building cloud and hybrid data platforms on AWS.
                I care about shipping reliable systems, making data easy to trust, and being a steady partner for teams
                from design to production.
              </div>

              <div className="aboutQuickRow">
                <span className="chip chip--solid">Data Engineering</span>
                <span className="chip chip--solid">AWS / Cloud</span>
                <span className="chip chip--solid">ETL / ELT</span>
                <span className="chip chip--solid">Analytics Enablement</span>
                <span className="chip chip--solid">Airflow / Cloudera(Hadoop)</span>
              </div>
            </div>
          </div>

          <div className="aboutDivider" />

          <div className="aboutStack">
            <Section
              title="Bio"
              subtitle="How I work, what I care about, and what teams can expect."
              open={openKey === "bio"}
              onToggle={() => setOpenKey((k) => (k === "bio" ? "" : "bio"))}
            >
            <p className="aboutText">
              I enjoy building data products that help teams move faster with confidence.
              I focus on practical, dependable engineering: clear architecture, strong data quality, and systems that are easy to operate.
              I like working closely with stakeholders and engineers to align on what good looks like, deliver iteratively, and keep things healthy in production with monitoring, alerting, and performance tuning.
              I also enjoy mentoring and sharing context, because the best results come from teams that learn and improve together.
            </p>

            <ul className="aboutList">
              <li>Own ingestion, transformation, and analytics ready datasets across AWS and hybrid environments, with a focus on clarity and reliability.</li>
              <li>Improve trust through data quality checks, validation automation, and production readiness with runbooks, alerts, and sensible SLAs.</li>
              <li>Support teams through collaboration and mentorship, helping align priorities and deliver outcomes that improve reliability, speed, and cost over time.</li>
            </ul>
            </Section>

            <Section
              title="Skills"
              subtitle="Tree + Timeline map (years on Y-axis, skills on X-axis)."
              open={openKey === "skills"}
              onToggle={() => setOpenKey((k) => (k === "skills" ? "" : "skills"))}
            >
              <SkillTree active={activeSkill} onChange={setActiveSkill} />
            </Section>

            <Section
              title="Education"
              subtitle="Foundation + continuous learning."
              open={openKey === "edu"}
              onToggle={() => setOpenKey((k) => (k === "edu" ? "" : "edu"))}
            >
              <div className="eduGrid">
                {education.map((edu) => (
                  <div key={edu.degree} className="eduCard">
                    <div className="eduHeader">
                      <div className="eduBadgeWrap">
                        <SmartImage candidates={[edu.image]} alt={edu.school} className="eduBadge" />
                      </div>
                      <div>
                        <div className="eduDegree">{edu.degree}</div>
                        <div className="eduSchool">
                          {edu.school} — {edu.year}
                        </div>
                      </div>
                    </div>
                    <div className="eduDetails">{edu.details}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title="Certifications"
              subtitle="Badges + Credly links."
              open={openKey === "certs"}
              onToggle={() => setOpenKey((k) => (k === "certs" ? "" : "certs"))}
            >
              <div className="certGrid">
                {certifications.map((cert) => (
                  <a
                    key={cert.name}
                    className="certCard"
                    href={cert.credly_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="certBadgeWrap">
                      <SmartImage candidates={[cert.badge_url]} alt={cert.name} className="certBadge" />
                    </div>
                    <div className="certName">{applyGlitchEffect(cert.name)}</div>
                    <div className="certLinkHint">View on Credly</div>
                  </a>
                ))}
              </div>
            </Section>

            <Section
              title="Languages"
              subtitle="Communication is part of engineering."
              open={openKey === "languages"}
              onToggle={() => setOpenKey((k) => (k === "languages" ? "" : "languages"))}
            >
              <div className="mediaGrid">
                {languages.map((l) => (
                  <div key={l.name} className="mediaCard">
                    <div className="mediaThumb">
                      <SmartImage candidates={[l.flag_image]} alt={l.name} className="flagBadge" />
                    </div>
                    <div className="mediaName">{applyGlitchEffect(l.name)}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title={applyGlitchEffect("Music")}
              subtitle="Soundtrack for deep work + downtime."
              open={openKey === "music"}
              onToggle={() => setOpenKey((k) => (k === "music" ? "" : "music"))}
            >
              <div className="mediaGrid">
                {music.map((m) => (
                  <div key={m.name} className="mediaCard">
                    <div className="mediaThumb">
                      <SmartImage candidates={[m.album_image]} alt={m.name} className="albumBadge" />
                    </div>
                    <div className="mediaName">{applyGlitchEffect(m.name)}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title={applyGlitchEffect("Citizenships")}
              subtitle="Background + passports."
              open={openKey === "citizenships"}
              onToggle={() => setOpenKey((k) => (k === "citizenships" ? "" : "citizenships"))}
            >
              <div className="mediaGrid mediaGrid--center">
                {citizenships.map((c) => (
                  <div key={c.name} className="mediaCard">
                    <div className="mediaThumb">
                      <SmartImage candidates={[c.flag_image]} alt={c.name} className="flagBadge" />
                    </div>
                    <div className="mediaName">{applyGlitchEffect(c.name)}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title={applyGlitchEffect("Citizenships")}
              subtitle="A little fun outside the data world."
              open={openKey === "games"}
              onToggle={() => setOpenKey((k) => (k === "games" ? "" : "games"))}
            >
              <div className="mediaGrid">
                {games.map((g) => (
                  <div key={g.name} className="mediaCard">
                    <div className="mediaThumb">
                      <SmartImage candidates={[g.game_image]} alt={g.name} className="gameBadge" />
                    </div>
                    <div className="mediaName">{applyGlitchEffect(g.name)}</div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
